// ═══════════════════════════════════════════════════════════════
// SHELTER MODULE — Families CRUD
// Family management: group survivors, track head of household
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, MultiSelect,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { ShelterFamily } from '../../types/shelter';

const EMPTY: Partial<ShelterFamily> = {
  familyName: '', headOfHouseholdId: '', memberIds: '[]', memberCount: 0, notes: '',
};

export default function Families() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('shelter');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    families, loadingFamilies, survivors,
    loadFamilies, loadSurvivors, createFamily, updateFamily, deleteFamily,
  } = useShelterStore();

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterFamily | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShelterFamily | null>(null);
  const [form, setForm] = useState<Partial<ShelterFamily>>({ ...EMPTY });
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadFamilies(); loadSurvivors(); }, [incidentId, loadFamilies, loadSurvivors]);

  // Store already server-side filters by active incident; use directly
  const iSurvivors = incidentId ? survivors : [];

  const survivorOptions = useMemo(
    () => iSurvivors.map(s => ({
      value: String(s.id),
      label: `${s.firstName} ${s.lastName}`.trim() || `Survivor #${s.id}`,
    })),
    [iSurvivors],
  );

  const survivorMap = useMemo(() => {
    const m = new Map<string, string>();
    survivors.forEach(s => m.set(String(s.id), `${s.firstName} ${s.lastName}`.trim()));
    return m;
  }, [survivors]);

  const iFamilies = incidentId ? families : [];

  const filtered = useMemo(() => {
    let list = iFamilies;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(f =>
        f.familyName.toLowerCase().includes(q) ||
        (survivorMap.get(f.headOfHouseholdId) ?? '').toLowerCase().includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return (a.familyName || '').localeCompare(b.familyName || '');
      const va = (a as any)[sortField] || '';
      const vb = (b as any)[sortField] || '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [iFamilies, search, survivorMap, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [search, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof ShelterFamily, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  const parseMemberIds = useCallback((json: string): string[] => {
    try { return JSON.parse(json || '[]'); } catch { return []; }
  }, []);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setSelectedMembers([]);
    setModalOpen(true);
  };

  const openEdit = (f: ShelterFamily) => {
    setEditTarget(f);
    setForm({ ...f });
    // memberIds is not persisted in V2 schema — derive from survivors that carry familyId
    const fromJson = parseMemberIds(f.memberIds);
    if (fromJson.length > 0) {
      setSelectedMembers(fromJson);
    } else {
      const fromSurvivors = iSurvivors
        .filter(s => s.familyId && s.familyId === String(f.id))
        .map(s => String(s.id));
      setSelectedMembers(fromSurvivors);
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.familyName?.trim()) {
      notifications.show({ message: 'Family name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        incidentId,
        memberIds: JSON.stringify(selectedMembers),
        memberCount: selectedMembers.length,
      };
      if (editTarget) {
        await updateFamily(editTarget.id, payload);
        notifications.show({ message: 'Family updated', color: 'green' });
      } else {
        await createFamily(payload);
        notifications.show({ message: 'Family created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteFamily(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.familyName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  // Derive family members from survivors with matching familyId
  // (memberIds field is not reliable in V2 schema; survivors carry the familyId link)
  const getMembersForFamily = (family: ShelterFamily): string[] => {
    const fromSurvivors = iSurvivors.filter(s => s.familyId && s.familyId === String(family.id));
    if (fromSurvivors.length > 0) {
      return fromSurvivors.map(s => `${s.firstName} ${s.lastName}`.trim() || `Survivor #${s.id}`);
    }
    // Fallback: parse stored memberIds JSON
    const ids = parseMemberIds(family.memberIds);
    return ids.map(id => survivorMap.get(id) ?? `#${id}`);
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Families</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingFamilies} onClick={() => loadFamilies()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Family</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search family name, head of household..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} famil{filtered.length !== 1 ? 'ies' : 'y'}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('familyName')}>Family Name{sortIcon('familyName')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('headOfHouseholdId')}>Head of Household{sortIcon('headOfHouseholdId')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Members</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Member Names</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  No families found
                </td></tr>
              )}
              {pageRows.map(f => {
                const names = getMembersForFamily(f);
                return (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }} onClick={() => openEdit(f)}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                      <Group gap={6}>
                        <IconUsers size={14} />
                        {f.familyName || '-'}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {survivorMap.get(f.headOfHouseholdId) || '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <Badge size="sm" variant="light">{names.length || f.memberCount}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Group gap={4}>
                        {names.length > 0 ? names.map((n, i) => (
                          <Badge key={i} size="xs" variant="dot">{n}</Badge>
                        )) : <Text size="xs" c="dimmed">None</Text>}
                      </Group>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(f)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(f)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Family' : 'Add Family'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Family Name" required value={form.familyName ?? ''}
              onChange={e => setField('familyName', e.target.value)} />
            <Select label="Head of Household" data={survivorOptions}
              value={form.headOfHouseholdId ?? ''}
              onChange={v => setField('headOfHouseholdId', v ?? '')}
              searchable clearable />
          </SimpleGrid>

          <MultiSelect label="Family Members" data={survivorOptions}
            value={selectedMembers} onChange={setSelectedMembers}
            searchable placeholder="Select survivors..." />

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Family'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Family" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.familyName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
